<template>
    <div :class="{['report-schedule-popup'] :true, ['hide'] : !display}">
        <div class="popup">
            <div class="title-section">
                <h2>{{ isEdit ? $t('dash.settings.notifications.reports.popup.edit_title') : $t('dash.settings.notifications.reports.popup.create_title') }}</h2>
            </div>

            <div class="container-input">
                <label>{{ $t('dash.settings.notifications.reports.popup.name.label') }}</label>
                <div class="input">
                    <i class="fa-regular fa-envelope"></i>
                    <input :placeholder="$t('dash.settings.notifications.reports.popup.name.placeholder')" v-model="form.name" maxlength="128">
                </div>
                <p class="help">{{ $t('dash.settings.notifications.reports.popup.name.help') }}</p>
                <div class="containerError" v-if="error.name">{{ error.name }}</div>
            </div>

            <div class="container-input">
                <label>{{ $t('dash.settings.notifications.reports.popup.frequency.label') }}</label>
                <div class="input">
                    <SelectComponent :changeValueSelect="changeFrequency" :data="frequencyOptions" :valueSelect="form.frequency" :positionY="'bottom'" :positionAuto="false" id="report-frequency"></SelectComponent>
                </div>
            </div>

            <div class="container-input" v-if="form.frequency === 'weekly'">
                <label>{{ $t('dash.settings.notifications.reports.popup.weekday.label') }}</label>
                <div class="weekdays">
                    <button
                        type="button"
                        v-for="(day, n) in weekdayOptions"
                        :key="n"
                        :class="{ active: `${form.weekday}` === `${n}` }"
                        :title="day.long"
                        @click="changeWeekday(n)"
                    >{{ day.short }}</button>
                </div>
            </div>

            <div class="container-input" v-if="form.frequency === 'interval'">
                <label>{{ $t('dash.settings.notifications.reports.popup.interval.label') }}</label>
                <div class="input">
                    <i class="fa-regular fa-calendar-days"></i>
                    <input type="number" min="2" max="90" step="1" v-model.number="form.interval_days">
                </div>
                <p class="help">{{ $t('dash.settings.notifications.reports.popup.interval.help') }}</p>
                <div class="containerError" v-if="error.interval">{{ error.interval }}</div>
            </div>

            <div class="container-input pages">
                <label>
                    {{ $t('dash.settings.notifications.reports.popup.pages.label') }}
                    <span class="toggle-all">
                        <span>{{ $t('dash.settings.notifications.reports.popup.pages.all') }}</span>
                        <ComponentSwitch :value="form.allPages" :onChange="toggleAllPages" id="report-all-pages"></ComponentSwitch>
                    </span>
                </label>
                <p class="help" v-if="form.allPages">{{ $t('dash.settings.notifications.reports.popup.pages.all_help') }}</p>

                <template v-else>
                    <div class="page-select">
                        <SelectPage
                            id="report-schedule"
                            :groups="selectData.groups"
                            :allPages="selectData.pagesById"
                            :SelectPageProp="selectedPageObjects"
                            v-on:updateSelectPage="updateSelectPage"
                        ></SelectPage>
                    </div>
                    <p class="selected-count">{{ $t('dash.settings.notifications.reports.popup.pages.selected', { n: form.page_ids.length }) }}</p>
                </template>
                <div class="containerError" v-if="error.pages">{{ error.pages }}</div>
            </div>

            <div class="container-input cc">
                <label>{{ $t('dash.settings.notifications.reports.popup.cc.label') }}</label>
                <div class="cc-field" :class="{ focused: ccFocused }" @click="$refs.ccInput.focus()">
                    <span class="cc-chip" v-for="email in form.cc_emails" :key="email">
                        {{ email }}
                        <i class="fa-solid fa-xmark" @click.stop="removeCc(email)"></i>
                    </span>
                    <input
                        ref="ccInput"
                        type="text"
                        v-model="ccQuery"
                        :placeholder="form.cc_emails.length ? '' : $t('dash.settings.notifications.reports.popup.cc.placeholder')"
                        @focus="ccFocused = true"
                        @blur="onCcBlur"
                        @keydown.enter.prevent="onCcEnter"
                        @keydown.tab="onCcTab"
                        @keydown.down.prevent="moveCcActive(1)"
                        @keydown.up.prevent="moveCcActive(-1)"
                        @keydown.backspace="onCcBackspace"
                        @keydown.esc="ccFocused = false"
                        @input="onCcInput"
                    >
                </div>
                <ul class="cc-suggestions" v-if="ccFocused && ccSuggestions.length">
                    <li
                        v-for="(user, i) in ccSuggestions"
                        :key="user.email"
                        :class="{ active: i === ccActive }"
                        @mousedown.prevent="addCc(user.email)"
                        @mouseenter="ccActive = i"
                    >
                        <span class="cc-user-name" v-if="user.name">{{ user.name }}</span>
                        <span class="cc-user-email">{{ user.email }}</span>
                    </li>
                </ul>
                <p class="help">{{ $t('dash.settings.notifications.reports.popup.cc.help') }}</p>
                <div class="containerError" v-if="error.cc">{{ error.cc }}</div>
            </div>

            <p class="send-info"><i class="fa-regular fa-clock"></i> {{ $t('dash.settings.notifications.reports.popup.send_info', { email: email }) }}</p>

            <div class="btns">
                <div class="btn-outline-primary" @click="$emit('closePopUp')">{{ $t('dash.settings.notifications.reports.popup.cancel') }}</div>
                <div class="btn-primary" :class="{ disabled: saving }" @click="save">{{ isEdit ? $t('dash.settings.notifications.reports.popup.save') : $t('dash.settings.notifications.reports.popup.create') }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import ComponentSwitch from './../utils/switch.vue'
import SelectComponent from './../utils/select.vue'
import SelectPage from './../SelectPage.vue'

const EMAIL_RE = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/
const MAX_CC = 10

// Lundi 12/10/2026 : sert à libeller les jours de la semaine dans la langue de l'interface
const MONDAY = new Date(2026, 9, 12)

export default {
    name: 'reportSchedulePopup',

    components: {
        ComponentSwitch,
        SelectComponent,
        SelectPage,
    },

    props: {
        display: Boolean,
        pages: {
            type: Array,
            default: () => []
        },
        groups: {
            type: Array,
            default: () => []
        },
        // bilan existant à modifier (sinon création)
        data: {
            type: Object,
            default: null
        },
        email: {
            type: String,
            default: ''
        },
        saving: {
            type: Boolean,
            default: false
        },
    },

    mounted() {
        // Utilisateurs du compte pour l'autocomplétion des copies
        if (!Object.keys(this.$store.getters['users/getUsers'] || {}).length) this.$store.dispatch('users/fetchUsers')
    },

    data() {
        return {
            form: this.initialForm(),
            error: { name: '', interval: '', pages: '', cc: '' },
            ccQuery: '',
            ccFocused: false,
            ccActive: 0,
        };
    },

    computed: {
        isEdit() {
            return !!(this.data && this.data.id)
        },

        frequencyOptions() {
            return {
                daily: this.$t('dash.settings.notifications.reports.popup.frequency.daily'),
                weekly: this.$t('dash.settings.notifications.reports.popup.frequency.weekly'),
                monthly: this.$t('dash.settings.notifications.reports.popup.frequency.monthly'),
                interval: this.$t('dash.settings.notifications.reports.popup.frequency.interval'),
            }
        },

        weekdayOptions() {
            const locale = this.$i18n.locale || 'fr'
            const longFormat = new Intl.DateTimeFormat(locale, { weekday: 'long' })
            const shortFormat = new Intl.DateTimeFormat(locale, { weekday: 'short' })
            const cap = label => label.charAt(0).toUpperCase() + label.slice(1)
            const options = {}
            for (let i = 1; i <= 7; i++) {
                const day = new Date(MONDAY)
                day.setDate(MONDAY.getDate() + i - 1)
                options[i] = { long: cap(longFormat.format(day)), short: cap(shortFormat.format(day).replace('.', '')) }
            }
            return options
        },

        // Même format que le store selectPage : groupes indexés par id, pages annotées de leurs groupes
        selectData() {
            const pagesById = {}
            this.pages.forEach(page => { pagesById[page.id] = { ...page, groups: [] } })
            const groups = this.groups.reduce((acc, group) => {
                const pages = (group.pages || []).filter(id => pagesById[id])
                pages.forEach(id => pagesById[id].groups.push(group.id))
                if (pages.length) acc[group.id] = { ...group, pages }
                return acc
            }, {})
            return { pagesById, groups }
        },

        // Utilisateurs du compte proposés en copie (hors user courant et adresses déjà ajoutées)
        ccSuggestions() {
            const users = Object.values(this.$store.getters['users/getUsers'] || {})
            const q = this.ccQuery.trim().toLowerCase()
            const taken = new Set(this.form.cc_emails.concat((this.email || '').toLowerCase()))
            return users
                .filter(u => u.email && !taken.has(u.email.toLowerCase()))
                .map(u => ({ email: u.email.toLowerCase(), name: [u.firstname, u.lastname].filter(Boolean).join(' ') }))
                .filter(u => !q || u.email.includes(q) || u.name.toLowerCase().includes(q))
                .slice(0, 6)
        },

        selectedPageObjects() {
            return this.form.page_ids.map(id => this.selectData.pagesById[id]).filter(Boolean)
        },
    },

    methods: {
        initialForm() {
            const d = this.data || {}
            const pageIds = Array.isArray(d.page_ids) ? [...d.page_ids] : []
            return {
                name: d.name || '',
                frequency: d.frequency || 'monthly',
                weekday: d.weekday || 1,
                interval_days: d.interval_days || 14,
                allPages: !pageIds.length,
                page_ids: pageIds,
                cc_emails: Array.isArray(d.cc_emails) ? [...d.cc_emails] : [],
            }
        },

        changeFrequency(value) {
            this.form.frequency = value
        },

        changeWeekday(value) {
            this.form.weekday = parseInt(value, 10)
        },

        toggleAllPages(checked) {
            this.form.allPages = checked
        },

        addCc(raw) {
            const email = String(raw || '').trim().toLowerCase()
            this.error.cc = ''
            if (!email) return true
            if (!EMAIL_RE.test(email)) {
                this.error.cc = this.$t('dash.settings.notifications.reports.popup.error.cc_invalid', { email })
                return false
            }
            if (this.form.cc_emails.length >= MAX_CC) {
                this.error.cc = this.$t('dash.settings.notifications.reports.popup.error.cc_max', { n: MAX_CC })
                return false
            }
            if (!this.form.cc_emails.includes(email)) this.form.cc_emails = [...this.form.cc_emails, email]
            this.ccQuery = ''
            this.ccActive = 0
            return true
        },

        removeCc(email) {
            this.form.cc_emails = this.form.cc_emails.filter(e => e !== email)
        },

        // Saisie libre : virgule, point-virgule ou espace valident l'adresse en cours
        onCcInput() {
            this.ccActive = 0
            if (!/[,;\s]/.test(this.ccQuery)) return
            const parts = this.ccQuery.split(/[,;\s]+/)
            const rest = parts.pop()
            parts.filter(Boolean).forEach(part => this.addCc(part))
            this.ccQuery = rest
        },

        onCcEnter() {
            if (this.ccQuery.trim()) {
                const match = this.ccSuggestions[this.ccActive]
                const typed = this.ccQuery.trim().toLowerCase()
                this.addCc(match && !EMAIL_RE.test(typed) ? match.email : typed)
            } else if (this.ccSuggestions[this.ccActive]) {
                this.addCc(this.ccSuggestions[this.ccActive].email)
            }
        },

        onCcTab(e) {
            if (this.ccQuery.trim()) {
                e.preventDefault()
                this.onCcEnter()
            }
        },

        onCcBackspace() {
            if (!this.ccQuery && this.form.cc_emails.length) this.form.cc_emails = this.form.cc_emails.slice(0, -1)
        },

        onCcBlur() {
            this.ccFocused = false
            if (this.ccQuery.trim()) this.addCc(this.ccQuery)
        },

        moveCcActive(step) {
            const n = this.ccSuggestions.length
            if (n) this.ccActive = (this.ccActive + step + n) % n
        },

        updateSelectPage(pageIds) {
            this.form.page_ids = pageIds
        },

        save() {
            if (this.saving) return
            this.error = { name: '', interval: '', pages: '', cc: '' }
            let valid = true

            // Adresse encore en cours de saisie
            if (this.ccQuery.trim() && !this.addCc(this.ccQuery)) valid = false

            if (!this.form.name.trim()) {
                valid = false
                this.error.name = this.$t('dash.settings.notifications.reports.popup.error.name_required')
            }

            const interval = parseInt(this.form.interval_days, 10)
            if (this.form.frequency === 'interval' && !(interval >= 2 && interval <= 90)) {
                valid = false
                this.error.interval = this.$t('dash.settings.notifications.reports.popup.error.interval')
            }

            if (!this.form.allPages && !this.form.page_ids.length) {
                valid = false
                this.error.pages = this.$t('dash.settings.notifications.reports.popup.error.pages_required')
            }

            if (!valid) return

            const payload = {
                name: this.form.name.trim(),
                frequency: this.form.frequency,
                weekday: this.form.frequency === 'weekly' ? this.form.weekday : null,
                interval_days: this.form.frequency === 'interval' ? interval : null,
                page_ids: this.form.allPages ? [] : this.form.page_ids,
                cc_emails: this.form.cc_emails,
            }
            if (this.isEdit) payload.id = this.data.id

            this.$emit('submit', payload)
        },
    },
};
</script>

<style lang="scss" scoped>
.report-schedule-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    &.hide {
        display: none;
    }

    .title-section {
        margin-bottom: 16px;

        h2 {
            font-size: 18px;
            font-weight: 700;
            color: #121826;
            margin: 0;
        }
    }

    .popup {
        background: #fff;
        border-radius: 12px;
        padding: 24px;
        width: 480px;
        max-width: 90vw;
        max-height: 90vh;
        overflow-y: auto;
    }

    .container-input {
        margin-bottom: 16px;

        label {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-weight: 600;
            font-size: 13px;
            margin-bottom: 6px;

            .toggle-all {
                display: flex;
                align-items: center;
                gap: 6px;
                font-weight: 400;
            }
        }

        .help {
            color: #9ba3b7;
            font-size: 12px;
            margin: 6px 0 0;
        }

        .input {
            display: flex;
            align-items: center;
            gap: 8px;
            border: 1px solid #e3e6ee;
            border-radius: 6px;
            padding: 8px 10px;

            input {
                flex-grow: 1;
                border: none;
                outline: none;
                font-size: 13px;
            }

            .componentSelect {
                flex-grow: 1;
            }

        }

        .weekdays {
            display: flex;
            gap: 6px;

            button {
                flex: 1;
                padding: 8px 0;
                border: 1px solid #e3e6ee;
                border-radius: 6px;
                background: #fff;
                font-size: 12.8px;
                font-weight: 600;
                color: #555e75;
                cursor: pointer;
                transition: background 0.15s, color 0.15s, border-color 0.15s;

                &:hover {
                    border-color: #b64985;
                    color: #6b1f4d;
                }

                &.active {
                    background: #6b1f4d;
                    border-color: #6b1f4d;
                    color: #fff;
                }
            }
        }

        .containerError {
            color: #e14e4e;
            font-size: 12px;
            margin-top: 4px;
        }
    }

    .page-select {
        height: 300px;
    }

    .selected-count {
        margin: 8px 0 0;
        font-size: 12px;
        color: #6b1f4d;
    }

    .cc {
        position: relative;

        .cc-field {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 6px;
            min-height: 38px;
            padding: 5px 8px;
            border: 1px solid #e3e6ee;
            border-radius: 6px;
            cursor: text;

            &.focused {
                border-color: #b64985;
            }

            input {
                flex: 1 1 140px;
                min-width: 120px;
                border: none;
                outline: none;
                font-size: 13px;
                padding: 3px 2px;
            }
        }

        .cc-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 3px 8px;
            border-radius: 12px;
            background: #f9f0f5;
            color: #6b1f4d;
            font-size: 12px;
            font-weight: 600;

            i {
                font-size: 11px;
                cursor: pointer;
                opacity: 0.7;

                &:hover {
                    opacity: 1;
                }
            }
        }

        .cc-suggestions {
            position: absolute;
            left: 0;
            right: 0;
            top: 64px;
            z-index: 20;
            margin: 0;
            padding: 4px;
            list-style: none;
            background: #fff;
            border: 1px solid #e0d0ea;
            border-radius: 6px;
            box-shadow: 0 6px 20px rgba(107, 31, 77, 0.12);

            li {
                display: flex;
                flex-direction: column;
                padding: 6px 8px;
                border-radius: 4px;
                cursor: pointer;

                &.active {
                    background: #f6f1fa;
                }
            }

            .cc-user-name {
                font-size: 13px;
                font-weight: 600;
                color: #121826;
            }

            .cc-user-email {
                font-size: 12px;
                color: #555e75;
            }
        }
    }

    .send-info {
        font-size: 12px;
        color: #555e75;
        margin: 0 0 12px;

        i {
            margin-right: 4px;
            color: #9ba3b7;
        }
    }

    .btns {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 8px;

        .disabled {
            opacity: 0.5;
            pointer-events: none;
        }
    }
}
</style>
